/**
 * PURPOSE: Estado de las versiones de uniformes de un torneo en "Asignar camisetas" de Programación (FTR-0046).
 * RESPONSIBILITIES: Cargar el estado del versionamiento (disponible / activo en el evento / deporte elegible) y las
 *   versiones con la que está en uso; crear una versión (copia de la que está en uso), usar otra y eliminar una que
 *   no está en uso, dejando la lista al día. Lo usa el modal "Asignar camisetas" (pestaña Versiones), el único lugar
 *   donde se gestionan las versiones. Los errores HTTP se propagan al llamador.
 * COLLABORATORS: uniformOptionsService.js, UniformVersionsPanel.jsx, AssignUniformsModal.jsx.
 * ROUTE: winscore/src/hooks/useUniformVersions.js
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getUniformErrorMessage } from "../admin/AdminCompany/uniformOptions.utils.js";
import {
  activateUniformVersion,
  createUniformVersion,
  deleteUniformVersion,
  listUniformVersions,
} from "../services/uniformOptionsService.js";

export const EMPTY_VERSION_STATE = Object.freeze({
  available: true,
  enabled: false,
  eligible: false,
  exclusiveStrategy: null,
  active: null,
  versions: [],
});

export const useUniformVersions = ({ idevent, idsport, enabled = true }) => {
  const [data, setData] = useState(EMPTY_VERSION_STATE);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled || !idevent || !idsport) {
      setData(EMPTY_VERSION_STATE);
      return EMPTY_VERSION_STATE;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const next = await listUniformVersions({ idevent, idsport });
      if (requestId === requestRef.current) setData(next);
      return next;
    } catch (err) {
      if (requestId !== requestRef.current) return null;
      if (err?.response?.data?.code === "VERSIONING_UNAVAILABLE") {
        setData({ ...EMPTY_VERSION_STATE, available: false });
      } else {
        setError(getUniformErrorMessage(err, "No se pudieron cargar las versiones de uniformes."));
      }
      return null;
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [enabled, idevent, idsport]);

  useEffect(() => {
    reload();
  }, [reload]);

  const run = useCallback(async (work) => {
    setBusy(true);
    try {
      return await work();
    } finally {
      setBusy(false);
    }
  }, []);

  /** Nueva versión: copia de la que está en uso, que pasa a ser la nueva en uso. Devuelve la versión creada. */
  const create = useCallback(
    (name) =>
      run(async () => {
        const result = await createUniformVersion({ idevent, idsport, name });
        setData({
          available: result.available,
          enabled: result.enabled,
          eligible: result.eligible,
          exclusiveStrategy: result.exclusiveStrategy,
          active: result.active,
          versions: result.versions,
        });
        return result.created;
      }),
    [idevent, idsport, run]
  );

  /** Usar una versión (los partidos cambian): devuelve el resultado del backend y recarga la lista. */
  const activate = useCallback(
    (version) =>
      run(async () => {
        const result = await activateUniformVersion(version.id);
        await reload();
        return result;
      }),
    [reload, run]
  );

  const remove = useCallback(
    (version) =>
      run(async () => {
        const next = await deleteUniformVersion(version.id);
        setData(next);
        return next;
      }),
    [run]
  );

  return {
    data,
    loading,
    busy,
    error,
    reload,
    create,
    activate,
    remove,
    // Solo con el versionamiento disponible, activo en el evento y un deporte colectivo.
    visible: Boolean(data.available && data.enabled && data.eligible),
  };
};
